import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Remove undefined and null values from an object
 * Useful for cleaning up data from Payload CMS before passing to components
 * 
 * @param obj - Object to clean
 * @returns Object with null/undefined values removed
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function cleanUndefined<T extends Record<string, any>>(obj: T): Partial<T> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cleaned: any = {};
  
  for (const key in obj) {
    const value = obj[key];
    
    // Skip null and undefined
    if (value === null || value === undefined) {
      continue;
    }
    
    // Recursively clean nested objects
    if (typeof value === "object" && !Array.isArray(value)) {
      const cleanedNested = cleanUndefined(value);
      // Only include if the nested object has properties
      if (Object.keys(cleanedNested).length > 0) {
        cleaned[key] = cleanedNested;
      }
    } 
    // Handle arrays
    else if (Array.isArray(value)) {
      const cleanedArray = value
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .map((item: any) => {
          if (typeof item === "object" && item !== null) {
            return cleanUndefined(item);
          }
          return item;
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .filter((item: any) => item !== null && item !== undefined);
      
      if (cleanedArray.length > 0) {
        cleaned[key] = cleanedArray;
      }
    } 
    // Keep other values (including false, 0, empty string)
    else {
      cleaned[key] = value;
    }
  }
  
  return cleaned;
}

/**
 * Remove null and undefined values from an object (shallow)
 * Faster alternative when you don't need deep cleaning
 * 
 * @param obj - Object to clean
 * @returns Object with null/undefined values removed
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function omitNullish<T extends Record<string, any>>(
  obj: T
): Partial<T> {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    if (value !== null && value !== undefined) {
      acc[key as keyof T] = value;
    }
    return acc;
  }, {} as Partial<T>);
}

/**
 * Format a date string for display
 * @param date - Date string or Date object
 * @returns Formatted date string
 */
export function formatDate(date: string | Date): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  return dateObj.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  })
}
